import { Backdrop, Checkbox, CircularProgress, Grid, MenuItem, Paper, TextField, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ActionButton } from "../../components/ActionButton/ActionButton";
import theme, { linkStyle, noArrows, red } from "../../theme";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  selectConsent,
  selectDob,
  selectEmail,
  selectEmailConfirm,
  selectRegNum,
  selectTerms,
  setDob,
  setEmail,
  setEmailConfirm,
  setRegNum,
  setTermsCond,
  reset
} from "./RegistrationSlice";
import {
  setMsg,
  selectMsg,
  setErrMsg,
  selectErrMsg,
  isLoading,
} from "../../redux/UserSlice";
import {
  startRegistration

} from "../../redux/UserAPI";
import { UserInfo } from "../../interfaces";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";

interface Errors {
  first_name: { error: boolean; err_msg: string | null };
  last_name: { error: boolean; err_msg: string | null };
  dob: { error: boolean; err_msg: string | null };
  email: { error: boolean; err_msg: string | null };
  regNum: { error: boolean; err_msg: string | null };
  terms: { error: { color: string }; err_msg: string | null };
  emailConfirm: { error: { color: string }; err_msg: string | null };
}

export function Registration() {

  const err_color: { color: string } = { color: "#4554A5" };
  const _errors: Errors = {
    first_name: { error: false, err_msg: null },
    last_name: { error: false, err_msg: null },
    dob: { error: false, err_msg: null },
    email: { error: false, err_msg: null },
    regNum: { error: false, err_msg: null },
    terms: { error: err_color, err_msg: null },
    emailConfirm: { error: err_color, err_msg: null },
  };
  const [err, setErr] = React.useState<string>("");
  const [errors, setErrors] = React.useState<Errors>(_errors);
  const [years, setYears] = useState<number[]>([]);
  const disabled = useAppSelector(selectConsent);
  const msg = useAppSelector(selectMsg);
  const err_msg = useAppSelector(selectErrMsg);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const confirm = useAppSelector(selectTerms);
  const DOB = useAppSelector(selectDob);
  const regNum = useAppSelector(selectRegNum);
  const email = useAppSelector(selectEmail);
  const emailConfirm = useAppSelector(selectEmailConfirm);
  const isLoadingUserSlice = useAppSelector(isLoading)

  const email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const string_regex = /^[a-zA-Z\-]+$/;
  const regNum_regex = /^\d{7}$/;

  useEffect(() => {

    const years = [];
    for (let i = 1900; i <= 1984; i++) {
      years.push(i);
    }
    setYears(years);
    if (msg) {
      dispatch(setMsg(null));
      return;
    }
  }, []);

  useEffect(() => {
    if (msg) {
      history.push("/");
    }
    if (err_msg) {
      return;
    }
  }, [msg, err_msg]);

  const days = [];
  const months = [];

  for (let d: number = 0; d < 31; d++) {
    days.push(d + 1);
  }
  for (let m: number = -1; m < 11; m++) {
    months.push(m + 1);
  }

  const checkYear = (year: number) => {
    if (year > 1990 || year < 1900) {
      return false;
    } else {
      return true;
    }
  };

  const submit = async () => {
    const validate = validation();
    if (!validate) {
      return;
    }

    const dob = `${DOB.year}-${(DOB.month + 1).toString().padStart(2, '0')}-${DOB.date.toString().padStart(2, '0')}`

    const userInfo: UserInfo = {
      DOB: dob,
      regNum: regNum as number,
      email: email as string,
      termsConditions: confirm,
      termsConditionsDate: new Date(),
      consent: confirm,
      consentDate: new Date(),
      emailConfirm: emailConfirm,
      emailConfirmDate: new Date(),
    };

    await dispatch(startRegistration(userInfo));
  };

  const validation = () => {
    let answer = true;
    const temp_err = _errors;

    if (!checkYear(DOB.year as number)) {
      temp_err.dob.error = true;
      temp_err.dob.err_msg = "Please enter a valid year";
      answer = false;
    }
    if (moment([DOB.year, DOB.month, DOB.date]).format("YYYY-MM-DD") === "Invalid date") {
      temp_err.dob.error = true;
      temp_err.dob.err_msg = "Please enter a valid date";
      answer = false;
    }
    if (!email || email.trim() === "") {
      temp_err.email.error = true;
      temp_err.email.err_msg = "Please enter an email address";
      answer = false;
    }
    if (email && !email_regex.test(email)) {
      temp_err.email.error = true;
      temp_err.email.err_msg = "Please enter a valid email address";
      answer = false;
    }
    if (!regNum || !regNum_regex.test(regNum.toString())) {
      temp_err.regNum.error = true;
      temp_err.regNum.err_msg = "Registration Number should be 7 digits";
      answer = false;
    }

    if (!confirm) {
      temp_err.terms.error = { color: red };
      const msg = "Please accept the Terms and Conditions";
      setErr(msg);
      answer = false;
    }
    if (!emailConfirm) {
      temp_err.emailConfirm.error = { color: red };
      const msg = "Please agree that we will contact you using the above email address";
      setErr(msg);
      answer = false;
    }

    setErrors({ ...temp_err });

    return answer;
  };

  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignContent={"flex-start"}
      alignItems="center"
      spacing={2}
      sx={{
        [theme.breakpoints.up("xl")]: {
          mt: '11em'
        },
        [theme.breakpoints.up("xs")]: {
          mt: '8em',
          mb: '5em'
        }, " @media (minWidth:400px)": { padding: 10 }
      }}
    >
      <Backdrop
        sx={{
          zIndex: 8000,
          color: "#fff",
        }}
        open={isLoadingUserSlice}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid
        item
        xs={11} md={5}
        sx={{ padding: "0px, 0px", " @media (minWidth:400px)": { padding: "0px, 20px" } }}
      >
        <Paper sx={{ padding: "0px, 0px", "@media (minWidth:400px)": { padding: "0px, 20px" } }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            style={{ padding: '1em', paddingTop: 0 }}
          >
            <Grid item xs={12} style={{ margin: "0 auto" }}>
              <Typography variant="h5" align="center">
                Create an Account
              </Typography>
            </Grid>
            <Grid xs={8} item style={{}}>
              <hr style={{ height: "1px", background: "black", width: "100%" }}></hr>
            </Grid>

            <Grid item xs={12} style={{ padding: 10, margin: "0 auto", paddingTop: 0 }}>
              <TextField
                error={errors.email.error}
                value={email}
                onChange={(e: any) => {
                  dispatch(setErrMsg(null));
                  dispatch(setEmail(e.target.value));
                }}
                fullWidth
                label={'Email'}
                helperText={errors.email.err_msg}
              />
            </Grid>

            <Grid xs={12} item style={{}}>
              <Typography variant="h6" align="center">
                Survivor's information
              </Typography>
              <TextField
                sx={noArrows}
                value={regNum}
                error={errors.regNum.error}
                onChange={(e: any) => {
                  dispatch(setErrMsg(null));
                  setErr("");

                  if (!/^[0-9]*$/.test(e.target.value)) {
                    return
                    const msg = "Registration number should contain only digits";
                    return setErr(msg);
                  }

                  if (/^\d{8}$/.test(e.target.value)) {
                    return
                    const msg = "Registration number should contain only 7 digits";
                    return setErr(msg);
                  }
                  dispatch(setRegNum(e.target.value));
                }}
                type="text"
                required
                fullWidth
                label={'Registration Number'}
                helperText={errors.regNum.err_msg}
              />
            </Grid>
            <Grid xs={12} item style={{ paddingBottom: 0 }}>
              <Typography variant="h6" align="center" >
                Date of Birth
              </Typography>
            </Grid>
            <Grid md={4} xs={12} item style={{ paddingTop: 0 }}>
              <TextField
                fullWidth
                error={errors.dob.error}
                type="number"
                value={DOB.date}
                select
                onChange={(e: any) => {
                  dispatch(setErrMsg(null));
                  dispatch(setDob({ ...DOB, date: e.target.value }));
                }}
                placeholder="Day"
              >
                {days.map((d, i) => {
                  return (
                    <MenuItem value={d} key={i}>
                      {d}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>

            <Grid md={4} xs={12} item style={{ paddingTop: 0 }}>
              <TextField
                type="number"
                error={errors.dob.error}
                fullWidth
                select
                onChange={(e: any) => {
                  dispatch(setErrMsg(null));
                  dispatch(setDob({ ...DOB, month: e.target.value }));
                }}
                required
                placeholder="Month"
                value={DOB.month}
              >
                {months.map((m, i) => {
                  return (
                    <MenuItem value={m} key={i}>
                      {moment().month(m).format("MMMM")}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            <Grid md={4} xs={12} item style={{ paddingTop: 0 }}>
              <TextField
                fullWidth
                error={errors.dob.error}
                type="number"
                select
                value={DOB.year}
                onChange={(e: any) => {
                  dispatch(setErrMsg(null));
                  dispatch(setDob({ ...DOB, year: e.target.value }));
                }}
                required
                placeholder="year"
              >
                {years.map((year, i) => {
                  return (
                    <MenuItem key={i} value={year}>
                      {year}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            <Grid xs={12} item style={{}}>
              <Typography color="error">{errors.dob.err_msg}</Typography>
            </Grid>

            <Grid xs={12} item style={{}}>
              <Grid container >
                < Grid item xs={1}>
                  <Checkbox
                    checked={confirm}
                    onChange={(e: any) => {
                      setErr("");
                      dispatch(setErrMsg(null));
                      dispatch(setTermsCond(!confirm));
                    }}
                  />
                </Grid>
                <Grid item xs={11} display={'flex'} alignItems={'center'}>
                  <Typography sx={errors.terms.error} textAlign={'left'} >
                    {`I have read and agree to the `}
                    <Link style={errors.terms.error} to="/terms_conditions">
                      terms and conditions
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={12} item style={{}}>
              <Grid container >
                < Grid item xs={1}>
                  <Checkbox
                    checked={emailConfirm}
                    onChange={(e: any) => {
                      setErr("");
                      dispatch(setErrMsg(null));
                      dispatch(setEmailConfirm(!emailConfirm));
                    }}
                  />
                </Grid>
                <Grid item xs={11} display={'flex'} alignItems={'center'}>
                  <Typography textAlign={'left'} sx={errors.emailConfirm.error}>I hereby allow the Claims Conference to contact me using the above email address</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={12} item style={{}}>
              <Typography variant="h6" textAlign={"center"}>
                <Link to="/consent">Please click here to agree to the processing of your personal data</Link>
              </Typography>
            </Grid>
          </Grid>
          <Grid xs={12} item style={{ padding: 5 }}>
            <Typography textAlign={"center"}>
              {`To view our privacy policy, click `}
              <a target="_blank" href="https://www.claimscon.org/about/privacy-policy/">
                here
              </a>
            </Typography>
          </Grid>
          <Grid xs={12} item sx={{ p: '1em' }}>
            <ActionButton disabled={!disabled} local_error={err} action={submit} name="Next" />
          </Grid>
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={2}

          >
            <Grid xs={12} item style={{ padding: '1em' }}>
              <Link style={linkStyle} to="/">
                Go Back
              </Link>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}